import React, { useEffect, useState } from 'react'

import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'

import styles from './../Dialog.module.sass'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import { nullifyAppointmentSchema } from './NullifyAppointmentSchema'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'

const NullifyAppointmentDialog = (props) => {
	const { i18n, pushNotification } = useGlobal()
	const { className, data, dismiss, resolve } = props
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const [loadingOptions, setLoadingOptions] = useState(true)
	const [connectionOptions, setConnectionOptions] = useState([])
	const [warningMessage, setWarningMessage] = useState(null)
	const formatDate = (iso) => {
		if (!iso) return ''
		const d = new Date(iso)
		return d.toLocaleDateString('pt-PT')
	}
	const highlightNumbers = (text) =>
		text
			.split(/(\d{5,})/g)
			.map((part, i) =>
				/^\d{5,}$/.test(part) ? <strong key={i}>{part}</strong> : part
			)
	const [selectedOption, setSelectedOption] = useState(null)
	const [selectionError, setSelectionError] = useState(false)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(nullifyAppointmentSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState } = methods

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}

		const hasOptions = connectionOptions.length > 0
		if (hasOptions && selectedOption === null) {
			setSelectionError('no-selection')
			return
		}
		if (!hasOptions && warningMessage) {
			setSelectionError('no-options')
			return
		}

		setSelectionError(null)
		const endpoint = `${API_URL}/api/clinical_processes/${data?.processId}/appointments/${data.appointmentId}/delete`

		setError(null)
		setSubmitting(true)

		const description = formData.description
		const idConsultaLigacao = selectedOption?.appointmentId
			? Number(selectedOption.appointmentId)
			: null

		const body = {
			description,
			idConsultaLigacao,
		}

		return fetch(
			endpoint,
			fetchOptions({
				method: 'POST',
				body,
				json: true,
			})
		)
			.then((r) => parseJSONContent(r))
			.then((result) => {
				const resultData =
					result?.['soap:Envelope']?.['soap:Body']?.[
						'DeleteAppointmentResponse'
					]?.['DeleteAppointmentResult']

				const sucesso = resultData?.['Sucesso']?._text === 'true'
				const mensagem = resultData?.['Mensagem']?._text
				if (!sucesso) {
					setSubmitting(false)
					setError([mensagem])
					return
				}

				setSubmitting(false)
				resolve?.()
				pushNotification(
					undefined,
					'success',
					i18n`Appointment nullified successfully!`
				)
			})
			.catch(() => {
				setSubmitting(false)
				setError([i18n`An error occurred. Please try again.`])
			})
	}

	useEffect(() => {
		const token =
			localStorage.getItem('session-token') ||
			sessionStorage.getItem('session-token')
		if (!token) return
		fetch(
			`${API_URL}/api/clinical_processes/${data.processId}/appointments/${data.appointmentId}/cancellation-link`,
			{
				method: 'GET',
				headers: { Authorization: `Bearer ${token}` },
			}
		)
			.then((r) => parseJSONContent(r))
			.then((res) => {
				if (res?.mensagem) {
					setError([highlightNumbers(res.mensagem)])
				} else {
					setConnectionOptions(res?.connectionOptions ?? [])
					if (res?.warningAppointmentId) {
						setWarningMessage(res.warningAppointmentId)
					}
				}
			})
			.catch(() => setError([i18n`An error occurred. Please try again.`]))
			.finally(() => setLoadingOptions(false))
	}, [])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]
				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<>
			{loadingOptions && (
				<div className={styles.loadingOverlay}>
					<div className={styles.spinner}></div>
					<div className={styles.loadingText}>{i18n`Loading`}</div>
				</div>
			)}
			<div style={{ opacity: loadingOptions ? 0 : 1 }}>
				<FormProvider {...methods}>
					<form
						id={'form_nullify_appointment'}
						onSubmit={handleSubmit(onSubmit)}
						className={`${styles['root'] || ''} ${className || ''}`}
						disabled={submitting}
					>
						<fieldset>
							{error && error.length > 0 && (
								<div className={styles['error-container']}>
									{error.map((err, i) => (
										<div
											key={i}
											className={styles['error-item']}
										>
											⚠ {err}
										</div>
									))}
								</div>
							)}
							<small className={styles['dialog-title']}>
								<strong>{i18n`Nullify reason`}</strong>
							</small>

							<HFTextInput
								placeholder={i18n`Request description`}
								name='description'
								multiline
								noResize
								rows='3'
								required
							/>

							{!loadingOptions &&
								(connectionOptions.length > 0 ||
									warningMessage) && (
									<>
										<small
											className={styles['dialog-title']}
										>
											<strong>{i18n`Decision link appointment`}</strong>
										</small>

										{warningMessage && (
											<p
												className={
													styles['warning-message']
												}
											>
												{i18n`A consulta`}{' '}
												{warningMessage}{' '}
												{connectionOptions.length > 0
													? i18n`vai ficar sem ligação a uma consulta destino. A qual pretende ligar?`
													: i18n`vai ficar sem ligação a uma consulta destino.`}
											</p>
										)}

										<div
											className={
												styles['cancellation-options']
											}
										>
											{connectionOptions.length > 0 && (
												<div
													className={
														styles['options-list']
													}
												>
													{connectionOptions.map(
														(opt) => (
															<div
																key={
																	opt.appointmentId
																}
																className={
																	styles[
																		'option-item'
																	]
																}
															>
																<label>
																	<input
																		type='radio'
																		name='connectionOption'
																		value={
																			opt.appointmentId
																		}
																		onChange={() => {
																			setSelectedOption(
																				opt
																			)
																			setSelectionError(
																				false
																			)
																		}}
																	/>
																	<div
																		className={
																			styles[
																				'option-details'
																			]
																		}
																	>
																		<strong>
																			#
																			{
																				opt.appointmentId
																			}
																		</strong>{' '}
																		–{' '}
																		{formatDate(
																			opt.date
																		)}
																		<br />
																	</div>
																</label>
															</div>
														)
													)}
												</div>
											)}

											{selectionError && (
												<div
													className={
														styles[
															'selection-error'
														]
													}
												>
													⚠{' '}
													{selectionError ===
													'no-options'
														? i18n`Não é possível anular aqui. Utilize o Portal para anular e marcar uma nova consulta.`
														: i18n`Por favor selecione uma opção de ligação`}
												</div>
											)}

											{connectionOptions.length === 0 && (
												<p
													className={
														styles['no-options']
													}
												>
													{i18n`There are no connection options available. To nullify, use the Portal where you can also schedule a new appointment.`}
												</p>
											)}
										</div>
									</>
								)}

							<small className={styles['nullify-message']}>
								{data.totalRequisitions
									? data.nullifyMessage
									: ''}{' '}
								{data.nullifyDerivedAppointments}
							</small>
						</fieldset>
					</form>
				</FormProvider>
			</div>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>

				<ActionButton
					form={`form_nullify_appointment`}
					type='submit'
					primary
					disabled={
						connectionOptions.length === 0 && !!warningMessage
					}
					style={
						connectionOptions.length === 0 && !!warningMessage
							? {
									opacity: 0.4,
									cursor: 'not-allowed',
									pointerEvents: 'auto',
							  }
							: undefined
					}
				>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default NullifyAppointmentDialog
